import { useAuth } from '@/services/auth';
import { useClientKey } from '@/state/agent';
import theme from '@/theme';
import { AppRoutes } from '@/utils/types';
import { IAgent } from '@d-id/types';
import styled from '@emotion/styled';
import { CircularProgress } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAgentsLibrary } from '../list-page/hooks';
import { Header } from './components';
import { AgentPreview } from './components/AgentPreview';

const AgentTemplateContainer = styled.div`
    display: flex;
    flex-direction: column;

    height: 100%;
    width: 100%;
    overflow: hidden;

    font-size: 16px;

    * {
        font-family: 'Manrope';
    }
`;

const StyledAgentPreview = styled(AgentPreview)`
    height: 100%;
    flex: 1;
    overflow: hidden;
    user-select: text;
    outline: none;
    margin: 0 auto;
    border: 1px solid ${theme.colors.black[300]};
    border-radius: 16px;
    isolation: isolate;

    width: auto;
    aspect-ratio: 16 / 9;
    max-width: 100%;
    max-height: 80vh;
`;

const ContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    justify-content: center;
`;

interface Props {
    agent: IAgent;
}

const AgentTemplatePage = ({ agent }: Props) => {
    return (
        <AgentTemplateContainer>
            <Header template={agent} />
            <ContentContainer>
                <StyledAgentPreview agentId={agent.id} from="agent-template-page" />
            </ContentContainer>
        </AgentTemplateContainer>
    );
};

const AgentTemplatePageWrapper = () => {
    const navigate = useNavigate();
    const { isAnonymous } = useAuth();
    const { agentId } = useParams();
    const { publicAgents, isLoading: isLoadingAgents } = useAgentsLibrary();
    const { isLoading: isLoadingClientKey } = useClientKey({ isAnonymous });

    const agent = publicAgents.find(agent => agent.id === agentId);

    if (isLoadingAgents || isLoadingClientKey) {
        return <CircularProgress size={96} />;
    }

    if (!agent) {
        navigate(AppRoutes.Agent);
        return null;
    }

    return <AgentTemplatePage agent={agent} />;
};

export { AgentTemplatePageWrapper as AgentTemplatePage };
